import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { midnight } from '../contestWizard/midnightStyles';

export function parsePercentFromCriterion(text: string): number | null {
    const m = String(text).match(/(\d+(?:\.\d+)?)\s*%/);
    if (!m) return null;
    const n = Number(m[1]);
    if (!Number.isFinite(n)) return null;
    return Math.min(100, Math.max(0, n));
}

export function criterionDisplayWeights(labels: string[]): number[] {
    if (!labels.length) return [];
    const parsed = labels.map(parsePercentFromCriterion);
    const numeric = parsed.filter((p): p is number => p !== null);
    if (numeric.length === labels.length && numeric.length > 0) {
        const sum = numeric.reduce((a, b) => a + b, 0);
        if (sum > 0) return numeric.map(p => (p / sum) * 100);
    }
    const n = labels.length;
    return labels.map(() => 100 / n);
}

export const editTriggerSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    fontFamily: '"Manrope", Inter, sans-serif',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: midnight.primary,
    borderRadius: 999,
    px: 1.25,
    py: 0.5,
    minWidth: 0,
    '&:hover': { bgcolor: 'rgba(255,201,101,0.1)' },
} as const;

export const primaryActionSx = {
    borderRadius: 999,
    px: 2.5,
    py: 0.75,
    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
    fontWeight: 800,
    fontSize: 12,
    textTransform: 'none',
    bgcolor: midnight.primary,
    color: '#392700',
    '&:hover': { bgcolor: '#ecaa00' },
    '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.35)', color: 'rgba(57,39,0,0.5)' },
} as const;

export const ghostActionSx = {
    borderRadius: 999,
    px: 2.5,
    py: 0.75,
    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'none',
    borderColor: 'rgba(255,255,255,0.22)',
    color: midnight.onSurface,
    '&:hover': { borderColor: 'rgba(255,201,101,0.45)', bgcolor: 'rgba(255,255,255,0.04)' },
} as const;

export const sectionShellSx = {
    bgcolor: midnight.surfaceLow,
    p: { xs: 2.5, sm: 3 },
    borderRadius: '12px',
    border: '1px solid rgba(72,72,71,0.22)',
};

export function DrawerSectionEditorActions({
    onCancel,
    onSave,
    saving,
    saveDisabled,
}: {
    onCancel: () => void;
    onSave: () => void;
    saving?: boolean;
    saveDisabled?: boolean;
}) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
            <Button type="button" variant="outlined" onClick={onCancel} disabled={saving} sx={ghostActionSx}>
                Cancel
            </Button>
            <Button
                type="button"
                variant="contained"
                disableElevation
                onClick={onSave}
                disabled={saving || saveDisabled}
                sx={primaryActionSx}>
                {saving ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: 'inherit' }} />
                        Saving…
                    </Box>
                ) : (
                    'Save'
                )}
            </Button>
        </Box>
    );
}
